import { apiClient } from './api';
import { db } from './firebase';
import { collection, query, where, getDocs, orderBy, doc, getDoc, limit, getCountFromServer } from 'firebase/firestore';
import { SettlementBatch, CreateSettlementBatchParams, MarkSettlementBatchPaidParams } from '../types/settlements';

/**
 * Listar lotes de liquidación (Directo a Firestore)
 */
export async function listBatches(params: { type?: string; status?: string; limit?: number; offset?: number; dateFrom?: string; dateTo?: string } = {}) {
    try {
        const limitVal = params.limit || 50;
        const offsetVal = params.offset || 0;

        // Construir el query base
        let q = query(
            collection(db, 'settlement_batches'),
            orderBy('createdAt', 'desc')
        );

        // Filtros básicos (tipo y estado)
        if (params.type) {
            q = query(q, where('type', '==', params.type));
        }
        if (params.status) {
            q = query(q, where('status', '==', params.status));
        }

        // Filtros de fecha usando Firestore where() en lugar de filtrado en memoria
        // Nota: Firestore requiere índices compuestos para queries con múltiples where() + orderBy
        if (params.dateFrom) {
            const fromTimestamp = new Date(params.dateFrom).getTime() / 1000;
            q = query(q, where('toDate.seconds', '>=', fromTimestamp));
        }
        if (params.dateTo) {
            const toTimestamp = new Date(params.dateTo).getTime() / 1000;
            q = query(q, where('toDate.seconds', '<=', toTimestamp));
        }

        // Optimización: Solo traer los documentos necesarios para la página actual
        // En lugar de traer TODOS y hacer slice, traemos hasta offset + limit
        const fetchLimit = offsetVal + limitVal;
        q = query(q, limit(fetchLimit));

        const snapshot = await getDocs(q);
        const allBatches = snapshot.docs.map(doc => ({ batchId: doc.id, ...doc.data() } as SettlementBatch));

        // Paginar en memoria solo los documentos que trajimos
        const paginated = allBatches.slice(offsetVal);

        // Para obtener el conteo total (necesario para calcular páginas),
        // usamos getCountFromServer que es mucho más eficiente que getDocs
        let countQuery = query(
            collection(db, 'settlement_batches'),
            orderBy('createdAt', 'desc')
        );
        if (params.type) {
            countQuery = query(countQuery, where('type', '==', params.type));
        }
        if (params.status) {
            countQuery = query(countQuery, where('status', '==', params.status));
        }
        if (params.dateFrom) {
            const fromTimestamp = new Date(params.dateFrom).getTime() / 1000;
            countQuery = query(countQuery, where('toDate.seconds', '>=', fromTimestamp));
        }
        if (params.dateTo) {
            const toTimestamp = new Date(params.dateTo).getTime() / 1000;
            countQuery = query(countQuery, where('toDate.seconds', '<=', toTimestamp));
        }
        const countSnapshot = await getCountFromServer(countQuery);
        const totalCount = countSnapshot.data().count;

        return {
            success: true,
            batches: paginated,
            count: totalCount, // Total real de documentos que cumplen los filtros
            limit: limitVal,
            offset: offsetVal
        };
    } catch (error) {
        console.error("Error listing settlement batches:", error);
        return { success: false, batches: [], count: 0 };
    }
}

/**
 * Obtener detalle de un lote (Directo a Firestore)
 */
export async function getBatch(batchId: string) {
    try {
        const docRef = doc(db, 'settlement_batches', batchId);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            throw new Error("Batch not found");
        }

        const itemsSnap = await getDocs(collection(db, 'settlement_batches', batchId, 'items'));
        const items = itemsSnap.docs.map(doc => ({ beneficiaryId: doc.id, ...doc.data() } as any));

        return {
            success: true,
            batch: { batchId: docSnap.id, ...docSnap.data() } as any,
            items
        };
    } catch (error) {
        console.error("Error getting settlement batch:", error);
        throw error;
    }
}

export async function createBatch(data: CreateSettlementBatchParams) {
    const response = await apiClient.post('/createSettlementBatch', data);
    return response.data;
}

export async function confirmBatch(batchId: string) {
    const response = await apiClient.post('/confirmSettlementBatch', { batchId });
    return response.data;
}

export async function markPaid(batchId: string, data: MarkSettlementBatchPaidParams) {
    const response = await apiClient.post('/markSettlementBatchPaid', { batchId, ...data });
    return response.data;
}

export const settlementsService = {
    listBatches,
    getBatch,
    createBatch,
    confirmBatch,
    markPaid
};
